import * as React from 'react';
import { RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getUsage } from '@/api/client';

type UsageRow = {
  username: string;
  display_name: string;
  role: string;
  status: string;
  checks: number;
  extracts: number;
  transcribe_seconds: number;
  rewrites: number;
  account_count: number;
};

export function UsageTab() {
  const [data, setData] = React.useState<{ month: string; users: UsageRow[] } | null>(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const d = await getUsage();
      setData(d);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>用量监控</CardTitle>
            <CardDescription>
              {data ? `当前月份: ${data.month}` : '各用户月度用量统计'}
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={load} title="刷新">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">用户</th>
                <th className="px-4 py-3 text-right font-medium">检测次数</th>
                <th className="px-4 py-3 text-right font-medium">提取次数</th>
                <th className="px-4 py-3 text-right font-medium">转录秒数</th>
                <th className="px-4 py-3 text-right font-medium">改写次数</th>
                <th className="px-4 py-3 text-right font-medium">监控账号</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="p-4" colSpan={6}><Skeleton className="h-8 w-full" /></td>
                </tr>
              ) : !data?.users?.length ? (
                <tr>
                  <td className="px-4 py-8 text-center text-muted-foreground" colSpan={6}>
                    暂无用量数据
                  </td>
                </tr>
              ) : (
                data.users.map((user, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="px-4 py-3">{user.display_name || user.username}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{user.checks ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{user.extracts ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{user.transcribe_seconds ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{user.rewrites ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{user.account_count ?? 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}